import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Text, Gap, Icon } from '@reservamos/elements';
import LocalizedCurrency from 'components/LocalizedCurrency';
import { useWhitelabelFeatures } from 'hooks/whitelabel/useWhitelabelFeatures';

/**
 * CapacityColumn component.
 *
 * Render the capacity column for ANTT mode.
 *
 * @param {object} props - hasCapacity, capacity.
 * @param {boolean} props.hasCapacity - Whether capacity data is available.
 * @param {number} props.capacity - The total capacity of seats in this category.
 * @returns {JSX.Element} The rendered component.
 */
const CapacityColumn = ({ hasCapacity, capacity }) => {
  const { t } = useTranslation('general');

  return (
    <Text size="S" color={hasCapacity ? 'grayStrong' : 'grayLight'}>
      {hasCapacity ? t('search:total_seats', { count: capacity }) : t('text.not_available')}
    </Text>
  );
};

CapacityColumn.propTypes = {
  hasCapacity: PropTypes.bool.isRequired,
  capacity: PropTypes.number,
};

CapacityColumn.defaultProps = {
  capacity: undefined,
};

/**
 * PriceColumn component.
 *
 * Render the price column for legacy mode.
 *
 * @param {object} props - isAvailable, validatedTotal.
 * @param {boolean} props.isAvailable - Whether the discount is available.
 * @param {number} props.validatedTotal - The validated total price.
 * @returns {JSX.Element} The rendered component.
 */
const PriceColumn = ({ isAvailable, validatedTotal }) => {
  const { t } = useTranslation('general');

  if (!isAvailable) {
    return (
      <Text size="S" italic>
        {t('text.not_available')}
      </Text>
    );
  }

  return (
    <LocalizedCurrency
      color="discount"
      price={validatedTotal}
      currencyStyle="currency"
      weight="semibold"
    />
  );
};

PriceColumn.propTypes = {
  isAvailable: PropTypes.bool.isRequired,
  validatedTotal: PropTypes.number.isRequired,
};

/**
 * DiscountRowItem component.
 *
 * Render a discount row item.
 *
 * Show discount as not available if:
 * - Availability is 0
 * - Total is not a number
 * Otherwise, show the discount.
 *
 * The third column behavior is controlled by feature flags:
 * - SHOW_CAPACITY_CATEGORIES: Shows total seats (capacity) - for Brazilian ANTT compliance
 * - SHOW_DISCOUNT_PRICE_COLUMN: Shows price (legacy behavior)
 *
 * @param {object} props - label, availability, total, capacity
 * @param {string} props.label - The label of the discount.
 * @param {number} props.availability - The available seats of the discount.
 * @param {number | string} props.total - The total price of the discount.
 * @param {number} props.capacity - Optional. The total capacity of seats in this category.
 * @returns {JSX.Element} The rendered component.
 */
const DiscountRowItem = ({ label, availability, total, capacity }) => {
  const { t } = useTranslation('general');
  const { SHOW_DISCOUNT_PRICE_COLUMN, SHOW_CAPACITY_CATEGORIES } = useWhitelabelFeatures();

  // Parse and validate total once to avoid double parsing and NaN issues
  const parsedTotal = Number(total);
  const isValidTotal = !Number.isNaN(parsedTotal);
  const validatedTotal = isValidTotal ? parsedTotal : 0;

  const noAvailable = availability === 0 || !isValidTotal;
  const seatContext = noAvailable ? 'zero' : '';

  // Check if capacity is available from backend
  const hasCapacity = Number.isFinite(capacity);

  return (
    <div className="provider-info-discounts-item">
      <Gap columnGap="XS" alignItems="center">
        <Icon type="Amenity" size="S" color={noAvailable ? 'grayLight' : 'accent'} />
        <span className="provider-info-discounts-label">
          <Text size="S">{label}</Text>
        </span>
      </Gap>
      {SHOW_CAPACITY_CATEGORIES && <CapacityColumn hasCapacity={hasCapacity} capacity={capacity} />}
      <Text size="S">{t('search:seat', { count: availability, context: seatContext })}</Text>
      {SHOW_DISCOUNT_PRICE_COLUMN && !SHOW_CAPACITY_CATEGORIES && (
        <PriceColumn isAvailable={!noAvailable} validatedTotal={validatedTotal} />
      )}
    </div>
  );
};

DiscountRowItem.propTypes = {
  label: PropTypes.string.isRequired,
  availability: PropTypes.number.isRequired,
  total: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
  capacity: PropTypes.number,
};

DiscountRowItem.defaultProps = {
  capacity: undefined,
};

export default DiscountRowItem;
